import { formatShortDate, type Connector, type ISODate } from '@app/shared'
import { connectorIsLive } from '@app/shared'

import { useConnectors, useDesktopWorker } from '../../lib/data'
import { agoPhrase } from '../../components/school/derive'
import { clockFromTimestamp } from '../../lib/time'
import { MJournal } from '../shell/icons'
import { Card, CardHeader } from '../shell/parts'

// ============================================================================
// 1b · SYNC HONESTY — docs/design/handoff-mobile/README.md §1b.
//
// THE ORDER OF THE RESULT IS THE DESIGN, and it is the order these components
// are rendered in:
//   1. the consequence banner (what broke, what that means, ONE action)
//   2. the warning strip   (what is throttled, and NO action, because there is
//      nothing for the owner to do about it)
//   3. the STILL CURRENT rule
//   4. what still works    (journal + memos, device-local)
//   5. stale content, kept visible and labelled — never blanked, never spun over
//   6. the footer, naming when everything was last actually current
//
// EVERY WORD COMES FROM THE CONNECTOR ROWS. `Connector.note.text` is the
// connector's own sentence (the data layer's one deliberate exception to
// "shapes carry facts, the view carries phrasing"), `outage.stale` is the plural
// noun phrase the banner completes, and the DATE in "are from Aug 21" is the day
// of `lastSyncAt` — never stored, so it cannot go stale about staleness.
// ============================================================================

export interface SyncHonesty {
  connectors: Connector[] | undefined
  /** Connectors that broke and made something stale. Each gets a banner and one action. */
  broken: Connector[]
  /** Throttled connectors. A strip, never a button. */
  throttled: Connector[]
  /**
   * The short date Canvas-derived content is FROM, or null when nothing is
   * stale. Drives the `.72` opacity and the `FROM AUG 21` pill on the cards.
   */
  staleFrom: string | null
  /** 'Last successful full sync: Aug 21, 6:04pm · calendar synced 4 min ago'. */
  footer: string | null
}

/**
 * The whole sync story for a screen, derived once.
 *
 * A FAILED READ OF THE CONNECTOR LIST IS NOT AN OUTAGE. If this read itself
 * fails, `broken` is empty and nothing is labelled stale — the app does not know
 * whether anything is wrong, and claiming an outage it cannot see would be as
 * dishonest as hiding one.
 */
export function useSyncHonesty(today: ISODate | undefined, nowMinutes: number): SyncHonesty {
  const { data: connectors } = useConnectors()

  const live = (connectors ?? []).filter(connectorIsLive)
  const broken = live.filter((c) => c.status === 'error' && c.outage !== null)
  const throttled = live.filter((c) => c.status === 'rate_limited')

  const stale = broken.find((c) => c.lastSyncAt !== null)
  const staleFrom = stale?.lastSyncAt ? formatShortDate(stale.lastSyncAt.slice(0, 10) as ISODate) : null

  let footer: string | null = null
  const synced = live.map((c) => c.lastSyncAt).filter((t): t is string => t !== null)
  if (synced.length > 0 && today) {
    // "Last successful FULL sync" is the oldest of the last-sync times: the most
    // recent moment at which every connector was current is the moment the
    // laggard last answered. Deriving it from the freshest one would call a
    // half-current app fully synced.
    const oldest = synced.reduce((a, b) => (a < b ? a : b))
    const newest = synced.reduce((a, b) => (a > b ? a : b))
    const newestOwner = live.find((c) => c.lastSyncAt === newest)
    footer =
      `Last successful full sync: ${formatShortDate(oldest.slice(0, 10) as ISODate)}, ${clockFromTimestamp(oldest)}` +
      (newestOwner && newest !== oldest
        ? ` · ${newestOwner.shortName} synced ${agoPhrase(newest, today, nowMinutes)}`
        : '')
  }

  return { connectors, broken, throttled, staleFrom, footer }
}

/**
 * 1 · THE CONSEQUENCE BANNER. Names what stopped, what that means for what is on
 * screen, and offers exactly one action.
 */
export function ConsequenceBanner({
  connector,
  onRetry,
  busy,
  error,
}: {
  connector: Connector
  /** Deliberately named `onRetry`, not `onReconnect` — see `PrimaryAction` in
   *  `components/connections/ConnectorCard.tsx` for why: `error` means the last
   *  sync failed, not that the credential expired, and re-running the sync is
   *  the one recovery act that is honest for every cause. This banner runs the
   *  same `syncConnector` mutation the card's `Try again` runs. */
  onRetry: (id: string) => void
  busy: boolean
  /** The last rejection from that mutation, or undefined/null when there is
   *  none to show. Owned by the caller (one `useSyncConnector()` per screen), so
   *  this component only renders whatever it is handed. */
  error?: unknown
}) {
  const from = connector.lastSyncAt
    ? formatShortDate(connector.lastSyncAt.slice(0, 10) as ISODate)
    : null

  return (
    <div className="flex flex-none flex-col gap-[10px] rounded-card border border-danger-line bg-danger-fill px-[15px] py-[14px]">
      <div className="flex items-center gap-2">
        <span className="size-[7px] rounded-full bg-danger-dot" aria-hidden />
        <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-danger-2">
          {connector.shortName} · error
        </span>
      </div>
      <p className="text-[13.5px] font-normal leading-[1.6] text-danger text-pretty">
        {/* The connector's own sentence when it wrote one — it knows what broke.
            Otherwise the consequence is assembled from the facts beside it, and
            it still names what is stale and that nothing was lost. */}
        {connector.note?.text ??
          `${connector.shortName} stopped syncing${from ? `, so ${connector.outage?.stale ?? 'everything it feeds'} below are from ${from}` : ''}. Nothing was lost — it just stopped updating.`}
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={() => onRetry(connector.id)}
        className="flex h-11 items-center justify-center rounded-tile bg-danger-solid text-[13.5px] font-semibold leading-none text-on-dark active:bg-danger-solid-2 disabled:opacity-60"
      >
        {busy ? 'Trying again…' : `Try ${connector.shortName} again`}
      </button>
      {/* THE RETRY THAT FAILED SAYS SO, IN WORDS, HERE — the same treatment the
          desktop banner gives it (routes/Connections.tsx PageBannerStrip): the
          button started the retry, so this banner owes the answer rather than a
          button that just stops spinning. It is appended AFTER the button, not
          inserted above or beside it, so the button's own position never moves —
          a tap already in flight on the button is never retargeted, and only the
          content further down the scroll shifts to make room. */}
      {error != null && (
        <p className="text-[12px] font-normal leading-[1.5] text-danger text-pretty">
          {error instanceof Error
            ? error.message
            : 'That retry could not be started, and the reason did not come back with a sentence attached.'}
        </p>
      )}
    </div>
  )
}

/** 2 · THE WARNING STRIP. No button — there is nothing for the user to do. */
export function ThrottleStrip({ connector }: { connector: Connector }) {
  return (
    <div className="flex flex-none items-start gap-[10px] rounded-card border border-warning-line bg-warning-fill px-[15px] py-3">
      <span className="mt-[5px] size-[7px] flex-none rounded-full bg-warning-dot" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold leading-[1.2] text-warning">
          {connector.shortName} is rate limited
        </div>
        <div className="mt-1 text-[12px] font-normal leading-[1.5] text-warning-2 text-pretty">
          {connector.note?.text ??
            (connector.retryAt
              ? `Retrying at ${clockFromTimestamp(connector.retryAt)}. Nothing to do.`
              : 'It retries on its own. Nothing to do.')}
        </div>
      </div>
    </div>
  )
}

/** 3 · The rule. Everything under it is current; everything above it is not. */
export function StillCurrentRule() {
  return (
    <div className="flex flex-none items-center gap-[9px] px-[2px]">
      <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-faint">
        Still current
      </span>
      <span className="h-px flex-1 bg-line" aria-hidden />
    </div>
  )
}

/**
 * 4 · WHAT STILL WORKS. The journal and voice memos never wait on a connector —
 * which is the fact that turns a sync failure from an outage into an
 * inconvenience, and it is the reason this app exists on a phone at all.
 */
export function LocalFirstCard() {
  const { data: worker } = useDesktopWorker()
  return (
    <Card className="gap-[10px]">
      <CardHeader
        icon={<MJournal className="flex-none text-journal" />}
        title="Journal & voice memos"
        right={
          <span className="text-[10.5px] font-medium leading-none text-success-dot">
            on this device
          </span>
        }
      />
      <p className="text-[12.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
        Writing and recording never wait on a connector. Memos queue locally and transcribe when{' '}
        {worker?.machine ?? 'your PC'} wakes up.
      </p>
    </Card>
  )
}

/** 6 · The footer. When everything was last actually current. */
export function SyncFooter({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p className="flex-none px-1 text-[11px] font-normal leading-[1.5] text-microcopy text-pretty">
      {text}
    </p>
  )
}
